import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { allShots, type Shot } from '@ai-studio/shared';

import { getView } from '../../engine/prism.js';
import { rawUrl } from '../../lib/thumbnails.js';
import { audioPlayingAt, audioStartsBetween, laneItems, overallState, sequencePreviewCommands, shotBadges, shotClip, shotThumb, snapTime, timecode, timelineAt, type Lane, type LaneItem } from '../../lib/video.js';
import { useVideo } from '../../state/video.js';
import { useWorkspace } from '../../state/workspace.js';
import { mediaUrl } from '../generation/previews/index.js';
import { IconButton, NumberField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

const LANES: { id: Lane; label: string }[] = [
  { id: 'dialogue', label: 'Dialogue' },
  { id: 'music', label: 'Music' },
  { id: 'sfx', label: 'SFX' },
  { id: 'ambience', label: 'Ambience' },
];

/** Starts / stops the finished audio pieces in step with the playhead (shot-by-shot playback). */
function useLanePlayback(items: readonly LaneItem[], projectId: number | null, version: string) {
  const playing = useVideo((s) => s.playing);
  const audios = useRef(new Map<string, HTMLAudioElement>());
  const last = useRef(0);
  useEffect(() => {
    const stopAll = () => {
      for (const a of audios.current.values()) a.pause();
      audios.current.clear();
    };
    if (!playing || projectId === null) {
      stopAll();
      return;
    }
    const start = (item: LaneItem, offset: number) => {
      if (audios.current.has(item.key)) return;
      const a = new Audio(rawUrl(projectId, item.file, version));
      a.currentTime = offset;
      void a.play().catch(() => undefined);
      a.onended = () => audios.current.delete(item.key);
      audios.current.set(item.key, a);
    };
    const t0 = useVideo.getState().time;
    for (const { item, offset } of audioPlayingAt(items, t0)) start(item, offset);
    last.current = t0;
    const off = useVideo.subscribe((s) => {
      const t = s.time;
      if (t < last.current || t - last.current > 1) {
        // A jump: restart what plays at the new time.
        stopAll();
        for (const { item, offset } of audioPlayingAt(items, t)) start(item, offset);
      } else for (const item of audioStartsBetween(items, last.current, t)) start(item, 0);
      last.current = t;
    });
    return () => {
      off();
      stopAll();
    };
  }, [playing, items, projectId, version]);
}

/** The player: the assembled cut, or shot by shot (clip, still, or the engine preview in the Scene view). */
function Player({ active }: { active: boolean }) {
  const projectId = useWorkspace((s) => s.projectId);
  const doc = useVideo((s) => s.doc)!;
  const pieces = useVideo((s) => s.pieces);
  const dir = useVideo((s) => s.outputDir);
  const version = useVideo((s) => s.version);
  const time = useVideo((s) => s.time);
  const playing = useVideo((s) => s.playing);
  const final = useVideo((s) => s.final);
  const sequence = useVideo((s) => s.sequence);
  const [mode, setMode] = useState<'shots' | 'cut'>('shots');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const loadedSeq = useRef<string | null>(null);
  const [engineNote, setEngineNote] = useState<string | null>(null);
  const { entries, total } = useMemo(() => useVideo.getState().timeline(), [doc]);
  const shots = useMemo(() => allShots(doc).map((r) => r.shot), [doc]);
  const at = timelineAt(entries, time);
  const shot: Shot | undefined = at ? shots[at.index] : undefined;
  const clip = shot ? shotClip(dir, shot, pieces) : null;
  const cut = mode === 'cut' && final;
  const src = projectId === null ? null : cut ? mediaUrl(projectId, final, version) : clip ? mediaUrl(projectId, clip.path, version) : null;
  const still = !cut && !clip && shot && projectId !== null ? shotThumb(dir, shot, pieces) : null;

  // Keep the <video> on the playhead when it is not the clock (paused, or a jump).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !at) return;
    const want = cut ? time : at.local;
    if (!playing || Math.abs(v.currentTime - want) > 0.3) v.currentTime = Math.max(0, want);
  }, [src, time, playing, cut, at?.index]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing && active) void v.play().catch(() => undefined);
    else v.pause();
  }, [playing, src, active]);

  // The clock: the playing <video> drives the playhead; without a clip, real time does.
  useEffect(() => {
    if (!playing || !active) return;
    let raf = 0;
    let prev = performance.now();
    const tick = (now: number) => {
      const s = useVideo.getState();
      const dt = (now - prev) / 1000;
      prev = now;
      const v = videoRef.current;
      const here = timelineAt(entries, s.time);
      let next: number;
      if (cut && v && !v.paused) next = v.currentTime;
      else if (!cut && v && src && !v.paused && here) {
        next = here.entry.start + v.currentTime;
        if (v.currentTime >= here.entry.duration - 0.02 || v.ended) next = here.entry.end + 0.001;
      } else next = s.time + dt;
      if (next >= total) {
        s.setTime(total);
        s.setPlaying(false);
        return;
      }
      s.setTime(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, active, entries, total, cut, src]);

  // Engine shots without a recording: drive the Scene view's sequence director.
  useEffect(() => {
    if (cut || !shot || shot.type !== 'engine' || clip) {
      setEngineNote(null);
      return;
    }
    const view = getView('scene');
    if (!view || !view.supportsSequences) {
      setEngineNote('Engine shot: open its scene in the Scene view (with an engine build that has sequences) to preview it, or record it.');
      return;
    }
    let live = true;
    void (async () => {
      for (const cmd of sequencePreviewCommands(sequence, time, loadedSeq.current)) {
        const state = await view.sequenceCommand(cmd);
        if (cmd.cmd === 'load') loadedSeq.current = state?.loaded ? sequence : null;
      }
      if (live) setEngineNote('Previewing in the Scene view (sequence director). Write the sequence first if it is missing.');
    })();
    return () => {
      live = false;
    };
  }, [cut, shot?.id, shot?.type, clip?.path, sequence, Math.round(time * 10)]);

  return (
    <div className="vs-player">
      <div className="vs-screen">
        {src ? (
          <video ref={videoRef} key={src} src={src} muted={!cut} playsInline preload="auto" className="vs-video" />
        ) : still ? (
          <img className="vs-video" src={rawUrl(projectId!, still, version)} alt="" />
        ) : (
          <div className="vs-noclip muted">
            <Icon name={shot?.type === 'engine' ? 'scene' : 'image'} size={22} />
            {shot ? `${shot.id}: no clip yet` : 'No shots'}
          </div>
        )}
        {engineNote && <div className="vs-engine-note">{engineNote}</div>}
        {!cut && clip && clip.state !== 'done' && <span className="vs-screen-tag">{clip.stage} · in review</span>}
        {!cut && clip && clip.state === 'done' && <span className="vs-screen-tag">{clip.stage}</span>}
      </div>
      <div className="vs-transport">
        <IconButton icon={playing ? 'pause' : 'play'} title={playing ? 'Pause (Space)' : 'Play (Space)'} active={playing} onClick={() => useVideo.getState().setPlaying(!playing)} />
        <IconButton icon="stop" title="Stop and rewind (Home)" onClick={() => { useVideo.getState().setPlaying(false); useVideo.getState().setTime(0); }} />
        <span className="mono vs-tc">
          {timecode(time)} / {timecode(total)}
        </span>
        {shot && <span className="muted">{shot.id}</span>}
        <span className="bar-spacer" />
        <div className="tb-group segmented" role="tablist" aria-label="Playback">
          <button type="button" role="tab" aria-selected={mode === 'shots'} className={`tb-btn${mode === 'shots' ? ' is-active' : ''}`} onClick={() => setMode('shots')} title="Play shot by shot with the voice, music and sound pieces">
            Shots
          </button>
          <button type="button" role="tab" aria-selected={mode === 'cut'} disabled={!final} className={`tb-btn${mode === 'cut' ? ' is-active' : ''}`} onClick={() => setMode('cut')} title={final ? `The assembled ${final}` : 'Assemble the video first'}>
            Final cut
          </button>
        </div>
        {shot?.type === 'engine' && (
          <button type="button" className="ghost small" onClick={() => void useVideo.getState().writeSequence().then(() => { loadedSeq.current = null; })} title="Write the sequence from the storyboard (cameras, animations, subtitles, audio)">
            <Icon name="refresh" size={12} /> Write sequence
          </button>
        )}
      </div>
    </div>
  );
}

export function VideoTimeline({ active }: { active: boolean }) {
  const projectId = useWorkspace((s) => s.projectId);
  const doc = useVideo((s) => s.doc);
  const pieces = useVideo((s) => s.pieces);
  const dir = useVideo((s) => s.outputDir);
  const version = useVideo((s) => s.version);
  const time = useVideo((s) => s.time);
  const selection = useVideo((s) => s.selection);
  const [pps, setPps] = useState(12);
  const scrubbing = useRef(false);
  const { entries, total } = useMemo(() => useVideo.getState().timeline(), [doc]);
  const items = useMemo(() => (doc ? laneItems(doc, dir, entries, pieces, total) : []), [doc, dir, entries, pieces, total]);
  useLanePlayback(items, projectId, version);
  if (!doc) return null;
  const shots = allShots(doc).map((r) => r.shot);
  const width = Math.max(200, total * pps + 40);
  const seekX = (clientX: number, el: Element) => {
    const box = el.getBoundingClientRect();
    useVideo.getState().setTime(snapTime((clientX - box.left) / pps, total, doc.fps));
  };
  const seekAt = (e: ReactPointerEvent<HTMLDivElement>) => seekX(e.clientX, e.currentTarget);
  const ticks: number[] = [];
  const step = pps >= 30 ? 1 : pps >= 10 ? 5 : pps >= 3 ? 15 : 60;
  for (let t = 0; t <= total; t += step) ticks.push(t);
  return (
    <div className="vs-timeline">
      <Player active={active} />
      <div className="vs-tl">
        <div className="vs-tl-tools">
          <span className="muted">Zoom</span>
          <div className="vs-num">
            <NumberField value={pps} min={1} max={120} step={1} integer onChange={setPps} title="Pixels per second" />
          </div>
        </div>
        <div className="vs-tl-scroll">
          <div className="vs-tl-inner" style={{ width }}>
            <div
              className="vs-ruler"
              onPointerDown={(e) => {
                scrubbing.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                seekAt(e);
              }}
              onPointerMove={(e) => scrubbing.current && seekAt(e)}
              onPointerUp={() => (scrubbing.current = false)}
            >
              {ticks.map((t) => (
                <span key={t} className="vs-tick" style={{ left: t * pps }}>
                  {timecode(t).replace(/\.0$/, '')}
                </span>
              ))}
            </div>
            <div className="vs-lane vs-lane-shots" onPointerDown={seekAt}>
              <span className="vs-lane-label">Shots</span>
              {entries.map((e, i) => {
                const sh = shots[i]!;
                const state = overallState(shotBadges(sh, pieces));
                const clip = shotClip(dir, sh, pieces);
                const thumb = shotThumb(dir, sh, pieces);
                return (
                  <div
                    key={e.shotId}
                    className={`vs-block is-${state}${selection.kind === 'shot' && selection.id === e.shotId ? ' is-selected' : ''}${sh.type === 'engine' ? ' is-engine' : ''}`}
                    style={{ left: e.start * pps, width: Math.max(2, e.duration * pps - 1) }}
                    title={`${e.shotId} · ${e.duration}s · ${clip ? clip.stage : 'no clip'}${e.transition !== 'cut' ? ` · ${e.transition} in` : ''}`}
                    onPointerDown={(ev) => {
                      ev.stopPropagation();
                      useVideo.getState().select({ kind: 'shot', id: e.shotId });
                      seekX(ev.clientX, ev.currentTarget.parentElement!);
                    }}
                  >
                    {thumb && projectId !== null && e.duration * pps > 40 && <img src={rawUrl(projectId, thumb, version)} alt="" />}
                    <span>{e.shotId}</span>
                  </div>
                );
              })}
            </div>
            {LANES.map((lane) => (
              <div key={lane.id} className="vs-lane" onPointerDown={seekAt}>
                <span className="vs-lane-label">{lane.label}</span>
                {items
                  .filter((i) => i.lane === lane.id)
                  .map((i) => (
                    <div key={i.key} className={`vs-block vs-audio is-${i.state}`} style={{ left: i.start * pps, width: Math.max(2, (i.end - i.start) * pps - 1) }} title={`${i.label} (${i.state})`}>
                      <span>{i.label}</span>
                    </div>
                  ))}
              </div>
            ))}
            <div className="vs-playhead" style={{ left: time * pps }} />
          </div>
        </div>
      </div>
    </div>
  );
}
